import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Typography,
  Stack,
  Pagination,
  Button,
  Container,
  Paper,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import { supabase } from "../supabaseClient";
import CharacterRankingsModal from "../components/CharacterRankingsModal"; // Add this import
import SEO from '../components/SEO';
import LazyImage from '../components/LazyImage';

// Add this utility function near the top (if not already present):
function normalizeString(str) {
  return (str || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9 ]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

export default function CharacterList() {
  const [characters, setCharacters] = useState([]);
  const [mangaOptions, setMangaOptions] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);
  const [genderOptions, setGenderOptions] = useState([]);
  const [affiliationOptions, setAffiliationOptions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState({
    manga: "",
    gender: "",
    role: "",
    affiliation: "",
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(9);
  const [totalCount, setTotalCount] = useState(0);
  const [globalScores, setGlobalScores] = useState({ power: [], popularity: [] });
  const [loading, setLoading] = useState(true);
  // Add new state for modal
  const [rankingsModalOpen, setRankingsModalOpen] = useState(false);
  const [selectedCharacterForRankings, setSelectedCharacterForRankings] = useState(null);

  useEffect(() => {
    const fetchDropdowns = async () => {
      const { data } = await supabase.from("dropdowns").select("*");
      const mangaRow = data.find((r) => r.category === "manga");
      const roleRow = data.find((r) => r.category === "role");
      const genderRow = data.find((r) => r.category === "gender");
      setMangaOptions(mangaRow?.options || []);
      setRoleOptions(roleRow?.options || []);
      setGenderOptions(genderRow?.options || []);
    };
    fetchDropdowns();
  }, []);

  useEffect(() => {
    const fetchAffiliations = async () => {
      if (!filter.manga) return setAffiliationOptions([]);
      const { data } = await supabase
        .from("dropdowns")
        .select("options")
        .eq("category", "affiliation")
        .eq("manga", filter.manga)
        .single();
      setAffiliationOptions(data?.options || []);
    };
    fetchAffiliations();
  }, [filter.manga]);

  useEffect(() => {
    // Keep only the global scores fetch
    const fetchGlobalScores = async () => {
      const { data } = await supabase
        .from("character_tag_scores")
        .select("character_id, score, type")
        .eq("tag", "Global");

      if (data) {
        setGlobalScores({
          power: data.filter((row) => row.type === "power_level").sort((a, b) => b.score - a.score),
          popularity: data.filter((row) => row.type === "popularity").sort((a, b) => b.score - a.score),
        });
      }
    };

    fetchGlobalScores();
  }, []);

  // New useEffect to fetch characters based on current page and filters:
  useEffect(() => {
    const fetchPaginatedCharacters = async () => {
      setLoading(true);

      // Build query with filters
      let query = supabase.from("characters").select(`
        id,
        name,
        manga,
        role,
        gender,
        affiliation,
        image_url
      `, { count: 'exact' });

      if (filter.manga) query = query.eq("manga", filter.manga);
      if (filter.gender) query = query.eq("gender", filter.gender);
      if (filter.role) query = query.eq("role", filter.role);
      if (filter.affiliation) query = query.eq("affiliation", filter.affiliation); // Changed from .contains()

      // Fetch all matching (filtered) characters
      const { data, error } = await query;
      if (error) {
        console.error("Error fetching characters:", error);
        setCharacters([]);
        setTotalCount(0);
        setLoading(false);
        return;
      }

      // Apply normalized search on the client side
      let filtered = data;
      if (searchTerm.trim()) {
        filtered = filtered.filter((char) =>
          normalizeString(char.name).includes(normalizeString(searchTerm))
        );
      }

      // Pagination
      const startIndex = (page - 1) * 9;
      const endIndex = startIndex + 8;
      setTotalCount(filtered.length);
      setCharacters(filtered.slice(startIndex, endIndex + 1));
      setLoading(false);
    };

    fetchPaginatedCharacters();
  }, [page, searchTerm, filter.manga, filter.gender, filter.role, filter.affiliation]);

  // Helper to get rank
  const getRank = (characterId, arr) => {
    const idx = arr.findIndex((row) => row.character_id === characterId);
    return idx >= 0 ? `${idx + 1}/${arr.length}` : `-/${arr.length}`;
  };

  // Pagination logic for filtered characters
  const pageCount = Math.ceil(totalCount / 9);
  const paginatedCharacters = characters; // Characters are already paginated from the query

  useEffect(() => {
    setPage(1);
  }, [searchTerm, filter.manga, filter.gender, filter.role, filter.affiliation]);

  const handlePageChange = (_, value) => {
    setPage(value);
    // Scroll to top of grid on page change for better UX
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Create unique key for character list content to trigger transitions
  const characterListKey = `${searchTerm}-${filter.manga}-${filter.gender}-${filter.role}-${filter.affiliation}-${page}`;

  // Animation variants
  const containerVariants = {
    hidden: { 
      opacity: 0,
      y: 20,
    },
    visible: { 
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeInOut",
        staggerChildren: 0.05,
      }
    },
    exit: { 
      opacity: 0,
      y: -20,
      transition: {
        duration: 0.3,
        ease: "easeInOut"
      }
    }
  };

  const itemVariants = {
    hidden: { 
      opacity: 0, 
      scale: 0.95,
      y: 20,
    },
    visible: { 
      opacity: 1, 
      scale: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    }
  };

  // Add handler for opening character rankings
  const handleViewCharacterRankings = (character) => {
    setSelectedCharacterForRankings(character);
    setRankingsModalOpen(true);
  };

  return (
    <>
      <SEO 
        title="Browse All Anime Characters & Series"
        description="Explore our complete database of anime and manga characters. Filter by series, power levels, and character types. Discover new characters to vote on!"
        keywords="anime character database, manga character list, browse anime characters, character catalog, anime series characters"
        url="/character-list"
      />
      <Box sx={{ minHeight: "100vh" }}> {/* Removed background property */}
        {/* Spacer for navbar */}
        <Box sx={{ height: { xs: 72, sm: 80 } }} />

        <Container maxWidth="xl" sx={{ py: { xs: 6, md: 8 } }}>
          {/* Title Section */}
          <Box sx={{ textAlign: "center", mb: { xs: 8, md: 12 } }}>
            <Typography
              variant="h1"
              sx={{
                fontSize: {
                  xs: "clamp(2.2rem, 8vw, 2.8rem)", // Responsive for mobile
                  sm: "clamp(2.8rem, 7vw, 4rem)",
                  md: 72
                },
                fontWeight: 900,
                background: "linear-gradient(45deg, #f49330 30%, #fff176 90%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                textAlign: "center",
                mb: 4,
                letterSpacing: 2,
              }}
            >
              CHARACTERS
            </Typography>
            <Typography
              variant="h5"
              sx={{
                color: "#e0e0e0",
                fontWeight: 600,
                fontSize: { xs: 18, sm: 22 },
                maxWidth: 600,
                mx: "auto",
                lineHeight: 1.4,
              }}
            >
              Explore the complete roster of manga characters
            </Typography>
          </Box>

          {/* Filter Controls Section */}
          <Box sx={{ 
            background: "rgba(35, 35, 35, 0.85)", // Made transparent
            borderRadius: 4,
            border: "2px solid #3e3e3e",
            p: { xs: 4, md: 6 },
            mb: { xs: 6, md: 8 },
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
            backdropFilter: "blur(10px)", // Add glass effect
            WebkitBackdropFilter: "blur(10px)",
          }}>
            <Typography 
              variant="h5" 
              sx={{ 
                color: "#fff", 
                fontWeight: 700, 
                mb: 4, 
                textAlign: "center",
                fontSize: { xs: 20, sm: 24 }
              }}
            >
              Filter Characters
            </Typography>

            <Stack 
              direction={{ xs: "column", sm: "row" }} 
              spacing={3} 
              sx={{ mb: 4, flexWrap: "wrap", justifyContent: "center" }}
            >
              <TextField
                label="Search by name"
                variant="outlined"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                sx={{ 
                  minWidth: 200, 
                  background: "#1a1a1a", 
                  borderRadius: 2,
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": { borderColor: "#555" },
                    "&:hover fieldset": { borderColor: "#f49330" },
                    "&.Mui-focused fieldset": { borderColor: "#f49330" },
                  },
                  "& .MuiInputLabel-root": { color: "#bbb" },
                  "& .MuiInputLabel-root.Mui-focused": { color: "#f49330" },
                }}
                InputProps={{ style: { color: "#fff" } }}
              />
              
              <FormControl sx={{ minWidth: 140 }}>
                <InputLabel sx={{ color: "#bbb", "&.Mui-focused": { color: "#f49330" } }}>Manga</InputLabel>
                <Select
                  value={filter.manga}
                  label="Manga"
                  onChange={e => setFilter(f => ({ ...f, manga: e.target.value }))}
                  sx={{ 
                    color: "#fff", 
                    background: "#1a1a1a", 
                    borderRadius: 2,
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#555" },
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#f49330" },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#f49330" },
                  }}
                >
                  <MenuItem value="">All Manga</MenuItem>
                  {mangaOptions.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                </Select>
              </FormControl>

              <FormControl sx={{ minWidth: 120 }}>
                <InputLabel sx={{ color: "#bbb", "&.Mui-focused": { color: "#f49330" } }}>Gender</InputLabel>
                <Select
                  value={filter.gender}
                  label="Gender"
                  onChange={e => setFilter(f => ({ ...f, gender: e.target.value }))}
                  sx={{ 
                    color: "#fff", 
                    background: "#1a1a1a", 
                    borderRadius: 2,
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#555" },
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#f49330" },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#f49330" },
                  }}
                >
                  <MenuItem value="">All Genders</MenuItem>
                  {genderOptions.map(g => <MenuItem key={g} value={g}>{g}</MenuItem>)}
                </Select>
              </FormControl>

              <FormControl sx={{ minWidth: 120 }}>
                <InputLabel sx={{ color: "#bbb", "&.Mui-focused": { color: "#f49330" } }}>Role</InputLabel>
                <Select
                  value={filter.role}
                  label="Role"
                  onChange={e => setFilter(f => ({ ...f, role: e.target.value }))}
                  sx={{ 
                    color: "#fff", 
                    background: "#1a1a1a", 
                    borderRadius: 2,
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#555" },
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#f49330" },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#f49330" },
                  }}
                >
                  <MenuItem value="">All Roles</MenuItem>
                  {roleOptions.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                </Select>
              </FormControl>

              <FormControl sx={{ minWidth: 140 }}>
                <InputLabel sx={{ color: "#bbb", "&.Mui-focused": { color: "#f49330" } }}>Affiliation</InputLabel>
                <Select
                  value={filter.affiliation}
                  label="Affiliation"
                  onChange={e => setFilter(f => ({ ...f, affiliation: e.target.value }))}
                  sx={{ 
                    color: "#fff", 
                    background: "#1a1a1a", 
                    borderRadius: 2,
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#555" },
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#f49330" },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#f49330" },
                  }}
                >
                  <MenuItem value="">All Affiliations</MenuItem>
                  {affiliationOptions.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
                </Select>
              </FormControl>

              <Button 
                variant="outlined" 
                onClick={() => setFilter({ manga: "", gender: "", role: "", affiliation: "" })}
                sx={{ 
                  color: "#fff", 
                  borderColor: "#555",
                  borderRadius: 2,
                  px: 3,
                  "&:hover": {
                    borderColor: "#f49330",
                    background: "rgba(244, 147, 48, 0.1)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                Clear Filters
              </Button>
            </Stack>
          </Box>

          {/* Active Filter Display */}
          {(searchTerm || filter.manga || filter.gender || filter.role || filter.affiliation) && (
            <Box sx={{ 
              textAlign: "center", 
              mb: 6, 
              p: 3, 
              background: "rgba(244, 147, 48, 0.1)",
              borderRadius: 3,
              border: "1px solid rgba(244, 147, 48, 0.3)"
            }}>
              <Typography sx={{ fontWeight: 600, color: "#f49330", fontSize: 18 }}>
                🔍 Active Filters: {totalCount} characters found
              </Typography>
            </Box>
          )}

          {/* Character Grid */}
          {loading ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <Typography sx={{ color: "#fff", textAlign: "center", fontSize: 24, py: 8 }}>
                Loading characters...
              </Typography>
            </motion.div>
          ) : (
            <>
              <AnimatePresence mode="wait">
                <motion.div
                  key={characterListKey}
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                >
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr", // Single column on mobile
                        sm: "repeat(auto-fit, minmax(340px, 1fr))",
                        md: "repeat(auto-fit, minmax(380px, 1fr))",
                      },
                      gap: 4,
                      justifyItems: "center",
                      mb: 8,
                      maxWidth: "1400px",
                      mx: "auto",
                      width: "100%",
                    }}
                  >
                    {paginatedCharacters.map((char, idx) => (
                      <motion.div key={char.id} variants={itemVariants}>
                        <Paper
                          sx={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            p: 3,
                            borderRadius: 4,
                            minHeight: 360,
                            width: { xs: "100%", sm: 340, md: 380 },
                            maxWidth: "100%",
                            gap: 3,
                            background: "rgba(35, 35, 35, 0.85)", // Made transparent
                            border: "2px solid #3e3e3e",
                            position: "relative",
                            overflow: "hidden",
                            transition: "all 0.3s ease",
                            backdropFilter: "blur(10px)", // Add glass effect
                            WebkitBackdropFilter: "blur(10px)",
                            "&:hover": {
                              borderColor: "#f49330",
                              boxShadow: "0 8px 32px rgba(244, 147, 48, 0.2)",
                              transform: "translateY(-4px)",
                            },
                          }}
                        >
                          <LazyImage
                            src={char.image_url}
                            alt={char.name}
                            threshold={0.2} // Load when 20% visible
                            onLoad={() => console.log(`✅ Loaded ${char.name}`)}
                            sx={{
                              width: 135,
                              height: 240,
                              borderRadius: 3,
                              border: "2px solid #444",
                              boxShadow: "0 4px 16px rgba(0,0,0,0.3)",
                              flexShrink: 0,
                            }}
                          />
                          <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", flex: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: "#fff", mb: 2 }}>
                              {char.name}
                            </Typography>
                            
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: "#e0e0e0", mb: 1 }}>
                                Manga: <Box component="span" sx={{ fontWeight: 400, color: "#bbb" }}>{char.manga}</Box>
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: "#e0e0e0", mb: 1 }}>
                                Role: <Box component="span" sx={{ fontWeight: 400, color: "#bbb" }}>{char.role}</Box>
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: "#e0e0e0", mb: 1 }}>
                                Gender: <Box component="span" sx={{ fontWeight: 400, color: "#bbb" }}>{char.gender}</Box>
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: "#e0e0e0" }}>
                                Affiliation: <Box component="span" sx={{ fontWeight: 400, color: "#bbb" }}>
                                  {Array.isArray(char.affiliation) ? char.affiliation.join(", ") : ""}
                                </Box>
                              </Typography>
                            </Box>

                            {/* Global Rank Box - Make clickable */}
                            <Box
                              onClick={() => handleViewCharacterRankings(char)}
                              sx={{
                                background: "rgba(244, 147, 48, 0.1)", 
                                borderRadius: 2, 
                                p: 2, 
                                border: "1px solid rgba(244, 147, 48, 0.3)",
                                cursor: "pointer",
                                position: "relative",
                                transition: "all 0.3s ease",
                                "&:hover": {
                                  background: "rgba(244, 147, 48, 0.2)",
                                  borderColor: "rgba(244, 147, 48, 0.5)",
                                  transform: "translateY(-1px)",
                                  "& .see-more-text": {
                                    color: "#fff",
                                    transform: "translateX(2px)",
                                  },
                                },
                              }}
                            >
                              <Typography variant="body2" sx={{ color: "#f49330", fontWeight: 600, mb: 1 }}>
                                Global Rankings
                              </Typography>
                              <Typography variant="caption" sx={{ fontWeight: 500, color: "#e0e0e0", display: "block" }}>
                                Power Level: <Box component="span" sx={{ fontWeight: 700, color: "#fff" }}>
                                  {getRank(char.id, globalScores.power)}
                                </Box>
                              </Typography>
                              <Typography variant="caption" sx={{ fontWeight: 500, color: "#e0e0e0", display: "block" }}>
                                Popularity: <Box component="span" sx={{ fontWeight: 700, color: "#fff" }}>
                                  {getRank(char.id, globalScores.popularity)}
                                </Box>
                              </Typography>
                              
                              {/* "See more" text */}
                              <Typography
                                className="see-more-text"
                                sx={{
                                  position: "absolute",
                                  bottom: 2, // Changed from 8 to 6
                                  right: 12,
                                  color: "#bbb",
                                  fontSize: 11,
                                  fontWeight: 500,
                                  transition: "all 0.3s ease",
                                }}
                              >
                                see more →
                              </Typography>
                            </Box>
                          </Box>
                        </Paper>
                      </motion.div>
                    ))}
                  </Box>
                </motion.div>
              </AnimatePresence>

              {characters.length === 0 && !loading && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  <Box sx={{ 
                    textAlign: "center", 
                    py: 8,
                    background: "rgba(35, 35, 35, 0.85)", // Made transparent
                    borderRadius: 4,
                    border: "2px solid #3e3e3e",
                    backdropFilter: "blur(10px)", // Add glass effect
                    WebkitBackdropFilter: "blur(10px)",
                  }}>
                    <Typography sx={{ color: "#e0e0e0", fontSize: 24, fontWeight: 600 }}>
                      No characters found
                    </Typography>
                    <Typography sx={{ color: "#bbb", fontSize: 16, mt: 2 }}>
                      Try adjusting your search criteria
                    </Typography>
                  </Box>
                </motion.div>
              )}

              {/* Pagination and Items Per Page Controls */}
              {pageCount > 1 && (
                <Box sx={{ 
                  display: "flex", 
                  flexDirection: { xs: "column", md: "row" },
                  justifyContent: "center", 
                  alignItems: "center",
                  gap: 4,
                  mt: 8 
                }}>
                  {/* Pagination */}
                  <Pagination
                    count={pageCount}
                    page={page}
                    onChange={handlePageChange}
                    size="large"
                    sx={{
                      "& .MuiPaginationItem-root": {
                        color: "#fff",
                        borderColor: "#555",
                        fontSize: 18,
                        height: 48,
                        minWidth: 48,
                        "&:hover": {
                          backgroundColor: "rgba(244, 147, 48, 0.1)",
                          borderColor: "#f49330",
                        },
                      },
                      "& .Mui-selected": {
                        background: "linear-gradient(45deg, #f49330 30%, #fff176 90%) !important",
                        color: "#181818 !important",
                        borderColor: "transparent !important",
                        fontWeight: 700,
                      },
                    }}
                  />
                </Box>
              )}
            </>
          )}
        </Container>

        {/* Character Rankings Modal */}
        <CharacterRankingsModal
          open={rankingsModalOpen}
          onClose={() => setRankingsModalOpen(false)}
          character={selectedCharacterForRankings}
        />
      </Box>
    </>
  );
}